import type { PageProps } from '@inertiajs/core';
import { Head, Link } from '@inertiajs/react';
import ProductRow from '@/components/ProductRow';
import { PlaceholderPattern } from '@/components/ui/placeholder-pattern';
import AppLayout from '@/layouts/app-layout';
import type { BreadcrumbItem } from '@/types';

const breadcrumbs: BreadcrumbItem[] = [
    {
        title: 'Товары',
        href: '/',
    },
];

type Product = {
    id: number;
    name: string;
    price: number;
    price_list: string;

    product_type: string;
    product_vendor: string;
    product_model: string;

    product_data: string;
};

type PaginationLink = {
    url: string | null;
    label: string;
    active: boolean;
};

type Paginated<T> = {
    data: T[];
    links: PaginationLink[];
    meta?: {
        current_page: number;
        from: number | null;
        last_page: number;
        path: string;
        per_page: number;
        to: number | null;
        total: number;
    };
};

type Props = PageProps & {
    title: string;
    products: Paginated<Product>;
    productsTotal: number;
};

export default function Main({ title, products, productsTotal }: Props) {

    const rows = products.data;

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={title} />
            <div className="flex h-full flex-1 flex-col gap-4 overflow-x-auto rounded-xl p-4">

                <div className="relative min-h-[100vh] flex-1 overflow-hidden rounded-xl border border-sidebar-border/70 md:min-h-min dark:border-sidebar-border">


                    <div
                        className="rounded-2xl border border-sidebar-border/70 bg-background shadow-sm overflow-hidden">

                        <div
                            className="rounded-2xl border border-sidebar-border/70 bg-background shadow-sm overflow-hidden">
                            <div className="px-6 py-4 border-b border-sidebar-border/50">
                                <h2 className="text-xl font-semibold tracking-tight">Товары</h2>

                                {products.meta ? (
                                    <p className="text-sm text-muted-foreground">
                                        Показано {products.meta.from ?? 0}–{products.meta.to ?? 0} из {products.meta.total}
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Всего: {productsTotal}</p>
                                )}
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40">
                                    <tr className="text-left">
                                        <th className="px-6 py-3 font-medium">ID</th>
                                        <th className="px-6 py-3 font-medium">Название</th>
                                        <th className="px-6 py-3 font-medium text-right">Цена</th>
                                        <th className="px-6 py-3 font-medium">Прайс-лист</th>
                                        <th className="px-6 py-3 font-medium"></th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {rows.length > 0 ? (
                                        rows.map((product) => (
                                            <ProductRow key={product.id} product={product} />
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={3} className="px-6 py-10 text-center text-muted-foreground">
                                                Нет товаров
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Пагинация */}
                            <div
                                className="flex items-center justify-between gap-3 px-6 py-4 border-t border-sidebar-border/50">
                                <div className="text-sm text-muted-foreground">
                                    {products.meta ? `Страница ${products.meta.current_page} из ${products.meta.last_page}` : null}
                                </div>

                                <nav className="flex items-center gap-1">
                                    {products.links.map((link, idx) => {
                                        const label = link.label
                                            .replace('&laquo;', '«')
                                            .replace('&raquo;', '»');

                                        const isDisabled = !link.url;

                                        return (
                                            <Link
                                                key={idx}
                                                href={link.url ?? ''}
                                                preserveScroll
                                                className={[
                                                    "inline-flex items-center rounded-lg px-3 py-2 text-sm border transition-colors",
                                                    link.active
                                                        ? "bg-primary text-primary-foreground border-primary"
                                                        : "bg-background hover:bg-muted/40 border-sidebar-border/60",
                                                    isDisabled ? "pointer-events-none opacity-50" : "",
                                                ].join(' ')}
                                                dangerouslySetInnerHTML={{ __html: label }}
                                            />
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>


                    </div>


                </div>

                <div
                    className="relative min-h-[100vh] flex-1 overflow-hidden rounded-xl border border-sidebar-border/70 md:min-h-min dark:border-sidebar-border">
                    <PlaceholderPattern
                        className="absolute inset-0 size-full stroke-neutral-900/20 dark:stroke-neutral-100/20" />
                </div>
                <div className="grid auto-rows-min gap-4 md:grid-cols-3">
                    <div
                        className="relative aspect-video overflow-hidden rounded-xl border border-sidebar-border/70 dark:border-sidebar-border">
                        <PlaceholderPattern
                            className="absolute inset-0 size-full stroke-neutral-900/20 dark:stroke-neutral-100/20" />
                    </div>
                    <div
                        className="relative aspect-video overflow-hidden rounded-xl border border-sidebar-border/70 dark:border-sidebar-border">
                        <PlaceholderPattern
                            className="absolute inset-0 size-full stroke-neutral-900/20 dark:stroke-neutral-100/20" />
                    </div>
                    <div
                        className="relative aspect-video overflow-hidden rounded-xl border border-sidebar-border/70 dark:border-sidebar-border">
                        <PlaceholderPattern
                            className="absolute inset-0 size-full stroke-neutral-900/20 dark:stroke-neutral-100/20" />
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
